/* Copyright (c) 2018-2023, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

export const STRUCTURED_JSON_PREFIX = 'json:';

/**
 * Native event types to identify the event
 */
export enum NativeEvents {
    ApplicationHide = 'APPLICATION_HIDE',
    ApplicationShow = 'APPLICATION_SHOW',
    WindowMinimize = 'MINIMIZE',
    WindowMaximize = 'MAXIMIZE',
    WindowRestore = 'RESTORE',
    IpcNewMessage = 'IPC_NEW_MESSAGE',
    SelfUpdate = 'SELF_UPDATE',
    RouteChange = 'URL_ROUTE_CHANGE',
    StoredCommandLine = 'STORED_COMMAND_LINE',
    ProcessEvent = 'PROCESS_EVENT',
    UpdateInitiated = 'UPDATE_INITIATED',
    SharedStorageUpdate = 'SHARED_STORAGE_UPDATE',
    ForegroundEvent = 'FOREGROUND_EVENT',
    DisplayModeChange = 'DISPLAYMODECHANGE'
}

/**
 * Base structured event.  More specific events can add further parsing by
 * inheriting from this class.
 */
export class NativeEvent {
    readonly type: string;
    readonly params: any;

    constructor(structuredEvent: any) {
        const { type, ...rest } = structuredEvent;
        this.type = type;
        this.params = rest;
    }
}

/**
 * Used for route change (or game changes)
 */
export class RouteChange extends NativeEvent {
    readonly newRoute: string;
    readonly currentRoute: string;

    constructor(structuredEvent: any) {
        super(structuredEvent);
        this.newRoute = structuredEvent['new-route'];
        this.currentRoute = structuredEvent['current-route'];
    }
}

/**
 * Used to indicate a game change in GFN (an alias of RouteChangeParameters)
 */
export type GameChange = RouteChange;

/**
 * Class to create native event instances from JSON structured event object
 */
export class NativeEventFactory {
    /**
     * Parse the structured native event to produce the specific structured event
     * object.
     *
     * @param event The structured native event (JSON blob) to parse
     * @return The StructuredEvent instance (or further derived instance)
     * holding the content of the event
     */
    static create(event: any): NativeEvent {
        // Add new cases as new derived types are defined.
        switch (event.type) {
            case NativeEvents.RouteChange:
                return new RouteChange(event);

            default:
                return new NativeEvent(event);
        }
    }
}
